/**
 * Edit Approval Rules — admin page for the edit-lock approval workflow.
 * ─────────────────────────────────────────────────────────────────────────────
 * Without rows in this master, every "Request Edit" request becomes orphaned
 * — there's no approver role configured to receive it. This page lets the
 * Admin / Plant Head configure who can approve edits on which entity type.
 *
 * Schema (entity_name, approver_role_id, plant_id?):
 *   entity_name   table name like 'suppliers' / 'customers' / 'items'
 *   approver_role one of the system roles (CFO, PURCHASE_MANAGER, etc.)
 *   plant_id      null = global rule (all plants); UUID = plant-specific
 *
 * Multiple roles can approve the same entity — anyone holding any of the
 * configured roles will see the request in their Approval Inbox.
 *
 * Changes here don't affect already-pending requests; new rules apply
 * to future requests only. (Pending requests carry their entity_name
 * forward and re-look-up the rule each time the inbox is rendered.)
 */
import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Empty, Modal, Popconfirm, Select, Space,
  Switch, Table, Tag, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined, PlusOutlined, ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { rolesApi } from '@/features/users/api'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

interface ApprovalRule {
  id: string
  entity_name: string
  approver_role_id: string
  approver_role_name?: string
  plant_id: string | null
  is_active: boolean
}

interface Role {
  id: string
  role_code: string
  role_name: string
}

// Common entity types from the system. The user can also type a custom
// entity_name in the create dialog (free-text) for entities not listed.
const ENTITY_TYPES = [
  { value: 'suppliers',           label: 'Suppliers (Vendors)' },
  { value: 'customers',           label: 'Customers' },
  { value: 'items',               label: 'Items / SKUs' },
  { value: 'item_master',         label: 'Item Master' },
  { value: 'plants',              label: 'Plants' },
  { value: 'companies',           label: 'Companies' },
  { value: 'departments',         label: 'Departments' },
  { value: 'machines',            label: 'Machines' },
  { value: 'count_masters',       label: 'Yarn Counts' },
  { value: 'stores',              label: 'Stores / Warehouses' },
  { value: 'numbering_series_master', label: 'Numbering Series' },
  { value: 'sales_orders',        label: 'Sales Orders' },
  { value: 'purchase_orders',     label: 'Purchase Orders' },
  { value: 'mixing_plans',        label: 'Mixing Plans' },
  { value: 'production_plans',    label: 'Production Plans' },
  { value: 'production_logs',     label: 'Production Logs' },
  { value: 'quality_tests',       label: 'Quality Tests' },
  { value: 'mill_lots',           label: 'Mill Lots' },
  { value: 'laydown_plans',       label: 'Laydown Plans' },
  { value: 'issue_slips',         label: 'Issue Slips' },
  { value: 'vouchers',            label: 'Vouchers' },
  { value: 'dispatch_orders',     label: 'Dispatch Orders' },
  { value: 'dispatch_invoices',   label: 'Dispatch Invoices' },
  { value: 'cotton_contracts',    label: 'Cotton Contracts' },
  { value: 'attendance',          label: 'Attendance' },
  { value: 'payroll_periods',     label: 'Payroll Periods' },
]

export default function EditApprovalRulesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [newEntity, setNewEntity] = useState<string>('')
  const [newRoleId, setNewRoleId] = useState<string | undefined>()

  // ─── Data ────────────────────────────────────────────────────────────
  const { data: rules = [], isLoading, refetch } = useQuery<ApprovalRule[]>({
    queryKey: ['edit-approval-rules'],
    queryFn: () =>
      apiClient.get('/edit-lock/rules').then(r => r.data),
  })

  // rolesApi.list takes no args + returns RoleOption[] directly (not paginated).
  // Defensive: if API returns paginated shape OR null/undefined, normalise to [].
  const { data: rolesRaw } = useQuery({
    queryKey: ['roles-all'],
    queryFn: () => rolesApi.list(),
  })
  const rolesArray: any[] = Array.isArray(rolesRaw)
    ? rolesRaw
    : Array.isArray((rolesRaw as any)?.items)
      ? (rolesRaw as any).items
      : []
  const roles: Role[] = rolesArray.map((r: any) => ({
    id: r.id,
    role_code: r.role_code ?? r.code ?? '',
    role_name: r.role_name ?? r.name ?? '',
  }))

  // Build a role_id → role meta map for table rendering
  const roleById = useMemo(() => {
    const m: Record<string, Role> = {}
    roles.forEach(r => { m[r.id] = r })
    return m
  }, [roles])

  // ─── Group rules by entity for the table ─────────────────────────────
  const grouped = useMemo(() => {
    const m = new Map<string, ApprovalRule[]>()
    rules.forEach(r => {
      const key = r.entity_name
      const arr = m.get(key) ?? []
      arr.push(r)
      m.set(key, arr)
    })
    return Array.from(m.entries())
      .map(([entity_name, items]) => ({ entity_name, items }))
      .sort((a, b) => a.entity_name.localeCompare(b.entity_name))
  }, [rules])

  // ─── Mutations ───────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: (body: any) =>
      apiClient.post('/edit-lock/rules', body).then(r => r.data),
    onSuccess: () => {
      message.success('Rule added — approvers can now act on requests')
      qc.invalidateQueries({ queryKey: ['edit-approval-rules'] })
      setCreateOpen(false)
      setNewEntity('')
      setNewRoleId(undefined)
    },
    onError: (e: any) =>
      message.error(e.response?.data?.detail || 'Add failed'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/edit-lock/rules/${id}`).then(r => r.data),
    onSuccess: () => {
      message.success('Rule removed')
      qc.invalidateQueries({ queryKey: ['edit-approval-rules'] })
    },
    onError: (e: any) =>
      message.error(e.response?.data?.detail || 'Remove failed'),
  })

  const handleCreate = () => {
    if (!newEntity || !newRoleId) {
      message.warning('Pick an entity type and an approver role')
      return
    }
    createMut.mutate({
      entity_name: newEntity.trim(),
      approver_role_id: newRoleId,
      plant_id: null,
    })
  }

  // ─── Columns ─────────────────────────────────────────────────────────
  type Group = { entity_name: string; items: ApprovalRule[] }
  const columns: ColumnsType<Group> = [
    {
      title: 'Entity Type', dataIndex: 'entity_name', key: 'entity', width: 220,
      render: (v: string) => {
        const meta = ENTITY_TYPES.find(e => e.value === v)
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{meta?.label ?? v}</Text>
            <Text type="secondary" style={{ fontSize: 10 }}>{v}</Text>
          </Space>
        )
      },
    },
    {
      title: 'Approver Roles', key: 'roles',
      render: (_: unknown, g) => (
        <Space wrap size={[4, 4]}>
          {g.items.length === 0 && <Tag color="red">⚠️ No approver — requests will be orphaned</Tag>}
          {g.items.map(r => {
            const role = roleById[r.approver_role_id]
            return (
              <Tag key={r.id}
                color={r.is_active ? 'blue' : 'default'}
                closable
                onClose={(e) => {
                  e.preventDefault()
                  deleteMut.mutate(r.id)
                }}>
                {role?.role_code ?? '?'} ({role?.role_name ?? 'unknown role'})
              </Tag>
            )
          })}
        </Space>
      ),
    },
    {
      title: 'Count', key: 'count', width: 90, align: 'right',
      render: (_: unknown, g) => <Text strong>{g.items.length}</Text>,
    },
  ]

  // ─── Quick check — entities WITHOUT any rule (orphan risk) ───────────
  const allConfiguredEntities = new Set(grouped.map(g => g.entity_name))
  const missingEntities = ENTITY_TYPES.filter(
    e => !allConfiguredEntities.has(e.value),
  )

  return (
    <App>
      <PageHeader
        title="Edit Approval Rules"
        subtitle="Who can approve 'Request Edit' submissions for which entity. Without rules → requests are orphaned."
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Add Rule
            </Button>
          </Space>
        }
      />

      {missingEntities.length > 0 && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message={`${missingEntities.length} entity types have NO approval rule yet`}
          description={
            <Space wrap size={[4, 4]} style={{ marginTop: 4 }}>
              {missingEntities.slice(0, 12).map(e => (
                <Tag key={e.value}>{e.label}</Tag>
              ))}
              {missingEntities.length > 12 && (
                <Text type="secondary">+ {missingEntities.length - 12} more</Text>
              )}
            </Space>
          }
        />
      )}

      <Card size="small">
        {!rules.length && !isLoading
          ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text>No approval rules configured.</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Click "Add Rule" to map an entity type to an approver role.
                    Without rules, every "Request Edit" stays orphaned.
                  </Text>
                </Space>
              }
            />
          )
          : (
            <Table
              rowKey="entity_name" size="small" loading={isLoading}
              dataSource={grouped} columns={columns}
              pagination={{ pageSize: 25, showTotal: t => `${t} entity types` }}
            />
          )}
      </Card>

      {/* Add rule dialog */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: '#1677ff' }} />
            Add Approval Rule
          </Space>
        }
        open={createOpen}
        onCancel={() => { setCreateOpen(false); setNewEntity(''); setNewRoleId(undefined) }}
        onOk={handleCreate}
        confirmLoading={createMut.isPending}
        okText="Add Rule"
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert type="info" showIcon
            message="Multiple approver roles can be added per entity. Anyone holding any of the configured roles will see edit-requests in their Approval Inbox." />

          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              Entity Type (table name)
            </Text>
            <Select
              showSearch allowClear
              style={{ width: '100%' }}
              placeholder="Pick or type an entity (e.g. suppliers)"
              value={newEntity || undefined}
              onChange={v => setNewEntity(v ?? '')}
              optionFilterProp="label"
              options={ENTITY_TYPES.map(e => ({
                value: e.value,
                label: `${e.label}  (${e.value})`,
              }))}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ padding: '4px 8px', fontSize: 11, color: '#888',
                                 borderTop: '1px solid #eee' }}>
                    Or type any other table name
                  </div>
                </>
              )}
              onSearch={(s) => setNewEntity(s)}
            />
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              Approver Role
            </Text>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder="Which role can approve edits on this entity?"
              value={newRoleId}
              onChange={setNewRoleId}
              optionFilterProp="label"
              options={roles.map(r => ({
                value: r.id,
                label: `${r.role_code} — ${r.role_name}`,
              }))}
            />
          </div>
        </Space>
      </Modal>
    </App>
  )
}
